<template>
  <v-card>
    <v-card-title class="d-flex align-center" v-if="!hideTitle">
      <v-icon left>mdi-calendar-month</v-icon>
      <h5 class="font-weight-bold text-h5 text-typo mb-0">本月打卡紀錄</h5>
      <v-spacer />
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        size="24"
      />
    </v-card-title>

    <v-card-text>
      <v-row>
        <!-- Year/Month Selection -->
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            label="年份"
            dense
            outlined
            hide-details
            class="mr-2"
            @change="onDateChange"
            style="max-width: 120px"
          ></v-select>
          <v-select
            v-model="selectedMonth"
            :items="monthOptions"
            label="月份"
            dense
            outlined
            hide-details
            @change="onDateChange"
            style="max-width: 100px"
          ></v-select>
        </v-col>

        <!-- User Selection -->
        <v-col cols="12" md="4" v-if="!hideUserSelect">
          <v-autocomplete
            v-model="selectedUserId"
            :items="users"
            :loading="loadingUsers"
            item-text="name"
            item-value="id"
            placeholder="請輸入姓名"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-magnify"
            clearable
            @change="onUserChange"
          >
            <template v-slot:label>
              <span class="red--text">搜尋員工</span>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.job_title }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col
          cols="12"
          md="4"
          class="text-right d-flex align-center justify-end"
        >
          <v-btn
            @click="exportExcelV2"
            elevation="0"
            :loading="exporting"
            :disabled="!selectedUserId || exporting"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-primary
              bg-gradient-primary
              py-3
              px-4
            "
          >
            <v-icon size="18" class="me-1">mdi-file-excel</v-icon>
            匯出月打卡紀錄
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Inner Three Tabs according to month.html -->
    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      color="#cb0c9f"
      class="mb-3 px-4"
    >
      <v-tab class="text-subtitle-2 font-weight-bold">日工時記錄</v-tab>
      <v-tab class="text-subtitle-2 font-weight-bold">事件記錄</v-tab>
      <v-tab class="text-subtitle-2 font-weight-bold">個人完整的打卡記錄</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="transparent">
      <!-- ================= Tab 1: 日工時記錄 (month.html a 樣) ================= -->
      <v-tab-item>
        <div class="px-4 pb-4">
          <div
            v-if="!selectedUserId"
            class="text-center grey--text py-8 text-body-2"
          >
            請選擇員工
          </div>
          <div v-else-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <div v-else class="daily-hours-matrix">
            <div
              v-for="(rowDays, rIdx) in dailyWorkHoursRows"
              :key="'matrix-row-' + rIdx"
              class="mb-4 overflow-x-auto"
            >
              <table class="matrix-table">
                <thead>
                  <tr>
                    <th
                      v-for="d in rowDays"
                      :key="'th-' + d.fullDate"
                      class="
                        text-center
                        py-2
                        px-1
                        text-caption
                        font-weight-bold
                      "
                      style="min-width: 60px"
                    >
                      {{ d.dateString }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      v-for="d in rowDays"
                      :key="'td-' + d.fullDate"
                      class="text-center py-2 px-1 text-sm"
                      :class="
                        d.isAlert ? 'red--text font-weight-bold' : 'text-body'
                      "
                      :title="d.holidayName || ''"
                    >
                      {{ d.displayValue }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </v-tab-item>

      <!-- ================= Tab 2: 事件記錄 (month.html b 樣) ================= -->
      <v-tab-item>
        <div class="px-4 pb-4">
          <div
            v-if="!selectedUserId"
            class="text-center grey--text py-8 text-body-2"
          >
            請選擇員工
          </div>
          <div v-else-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <div v-else class="overflow-x-auto">
            <table class="stats-table mb-4">
              <tbody>
                <!-- Row 1: 正常工時, 平日加班, 國定加班, 休息加班, 曠職, 遲到, 早退, 事假, 家庭照顧, 病假 -->
                <tr>
                  <td class="stat-hdr">正常工時</td>
                  <td class="stat-val">{{ monthlyStats.regular_hours }}</td>
                  <td class="stat-hdr">平日加班</td>
                  <td class="stat-val">{{ monthlyStats.weekday_overtime }}</td>
                  <td class="stat-hdr">國定加班</td>
                  <td class="stat-val">{{ monthlyStats.national_overtime }}</td>
                  <td class="stat-hdr">休息加班</td>
                  <td class="stat-val">{{ monthlyStats.recess_overtime }}</td>
                  <td class="stat-hdr">曠職</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.absent_hours > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.absent_hours }}
                  </td>
                  <td class="stat-hdr">遲到</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.late_count > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.late_count }}
                  </td>
                  <td class="stat-hdr">早退</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.early_count > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.early_count }}
                  </td>
                  <td class="stat-hdr">事假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.personal_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.personal_leave }}
                  </td>
                  <td class="stat-hdr">家庭照顧</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.family_care_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.family_care_leave }}
                  </td>
                  <td class="stat-hdr">病假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.sick_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.sick_leave }}
                  </td>
                </tr>
                <!-- Row 2: 生理假, 公傷, 公假, 產假, 產檢假, 陪產及檢, 喪假, 婚假, 原民祭儀, 特休假 -->
                <tr>
                  <td class="stat-hdr">生理假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.menstrual_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.menstrual_leave }}
                  </td>
                  <td class="stat-hdr">公傷</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.work_injury_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.work_injury_leave }}
                  </td>
                  <td class="stat-hdr">公假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.official_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.official_leave }}
                  </td>
                  <td class="stat-hdr">產假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.maternity_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.maternity_leave }}
                  </td>
                  <td class="stat-hdr">產檢假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.prenatal_check_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.prenatal_check_leave }}
                  </td>
                  <td class="stat-hdr">陪產及檢</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.paternity_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.paternity_leave }}
                  </td>
                  <td class="stat-hdr">喪假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.funeral_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.funeral_leave }}
                  </td>
                  <td class="stat-hdr">婚假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.marriage_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.marriage_leave }}
                  </td>
                  <td class="stat-hdr">原民祭儀</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.indigenous_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.indigenous_leave }}
                  </td>
                  <td class="stat-hdr">特休假</td>
                  <td
                    class="stat-val"
                    :class="
                      monthlyStats.annual_leave > 0
                        ? 'red--text font-weight-bold'
                        : ''
                    "
                  >
                    {{ monthlyStats.annual_leave }}
                  </td>
                </tr>
                <!-- Row 3: 特別摘要 -->
                <tr>
                  <td
                    class="stat-hdr font-weight-bold"
                    style="vertical-align: middle"
                  >
                    特別摘要
                  </td>
                  <td colspan="19" class="pa-3 text-left">
                    <div class="text-body-2">
                      <div class="font-weight-bold mb-1">
                        本欄位統計年度內上列表格的累積總時數
                      </div>
                      <div class="mb-1 text-caption grey--text text--darken-1">
                        注意事項：
                      </div>
                      <div
                        class="mb-1"
                        :class="
                          annualSpecialSummary.sickLeaveOverLimit
                            ? 'red--text font-weight-bold'
                            : ''
                        "
                      >
                        1. 病假(sick)：{{ annualSpecialSummary.sickHours }}小時（{{
                          annualSpecialSummary.sickDays
                        }}天）/ {{ annualSpecialSummary.sickCount }}次
                        <span class="text-caption">
                          （※累積超過10日或80小時，以紅字顯示。只記錄一般病假，前24小時的生理假不計入。）
                        </span>
                      </div>
                      <div class="mb-1">
                        2. 生理假(menstrual)：{{
                          annualSpecialSummary.menstrualHours
                        }}小時（{{ annualSpecialSummary.menstrualDays }}天）/
                        {{ annualSpecialSummary.menstrualCount }}次
                      </div>
                      <div>
                        3. 家庭照顧(familyCare)：{{
                          annualSpecialSummary.familyCareHours
                        }}小時（{{ annualSpecialSummary.familyCareDays }}天）/
                        {{ annualSpecialSummary.familyCareCount }}次
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </v-tab-item>

      <!-- ================= Tab 3: 個人完整的打卡記錄 (month.html c 樣) ================= -->
      <v-tab-item>
        <v-data-table
          :headers="headers"
          :items="records"
          :loading="loading"
          :server-items-length="totalItems"
          :items-per-page="itemsPerPage"
          class="table"
          hide-default-footer
          disable-pagination
          @page-count="pageCount = $event"
          @update:items-per-page="handleItemsPerPageChange"
        >
          <template v-slot:progress>
            <v-progress-linear indeterminate color="primary" />
          </template>

          <!-- Custom Date Column: M/D(Weekday) -->
          <template v-slot:item.date="{ item }">
            <span class="text-sm font-weight-bold text-body">
              {{ formatDateWithWeekday(item.clock_date) }}
            </span>
          </template>

          <!-- Timeline Status Slot (demo.html / month.html: 狀態) -->
          <template v-slot:item.timeline_status="{ item }">
            <div class="d-flex align-center flex-wrap py-1" style="gap: 8px">
              <template v-for="event in getTimelineEvents(item)">
                <!-- GPS Menu for events with coordinates -->
                <v-menu
                  v-if="isValidGPS(event.gps)"
                  :key="event.id"
                  :value="activeGPSKey === event.gpsKey"
                  @input="(val) => (activeGPSKey = val ? event.gpsKey : null)"
                  offset-y
                  :close-on-content-click="false"
                  min-width="250"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      :class="[
                        'text-sm font-weight-medium',
                        event.isAlert
                          ? 'red--text'
                          : 'text-body font-weight-bold',
                      ]"
                      style="cursor: pointer; border-bottom: 1px dashed #cb0c9f"
                      :title="'點擊查看 GPS: ' + event.gps"
                    >
                      {{ event.label }}
                    </span>
                  </template>
                  <v-card class="card-shadow border-radius-xl">
                    <v-card-title
                      class="pa-2 d-flex justify-space-between align-center"
                    >
                      <span class="text-subtitle-2 ml-2">{{
                        event.gpsTitle || "GPS 位置"
                      }}</span>
                      <v-btn icon x-small @click="activeGPSKey = null">
                        <v-icon size="16">mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="pa-3">
                      <div class="mb-3 text-body-2 font-weight-bold">
                        {{ event.gps }}
                      </div>
                      <v-btn
                        block
                        small
                        color="primary"
                        class="bg-gradient-primary"
                        @click="openGPSInGoogleMaps(event.gps)"
                      >
                        <v-icon left size="16">mdi-map-marker</v-icon>
                        開啟地圖
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-menu>

                <!-- Standard text item without GPS -->
                <span
                  v-else
                  :key="event.id"
                  :class="[
                    'text-sm',
                    event.isAlert ? 'red--text font-weight-bold' : 'text-body',
                  ]"
                >
                  {{ event.label }}
                </span>
              </template>
            </div>
          </template>

          <!-- Notes Slot (demo.html / month.html: 備註) -->
          <template v-slot:item.notes="{ item }">
            <div class="d-flex flex-wrap align-center py-1" style="gap: 6px">
              <span
                v-for="(n, idx) in getNotesInfo(item)"
                :key="idx"
                :class="[
                  'text-sm',
                  n.isAlert ? 'red--text font-weight-bold' : '',
                ]"
                :style="n.color ? { color: n.color } : {}"
              >
                {{ n.text }}
              </span>
            </div>
          </template>

          <!-- Header Slots for compact numeric columns (ignore header text width) -->
          <template v-slot:header.regular_hours>
            <span class="d-inline-block text-center header-multiline"
              >正常<br />工時</span
            >
          </template>
          <template v-slot:header.overtime_tier1>
            <span class="d-inline-block text-center header-multiline"
              >前2小時<br />加班</span
            >
          </template>
          <template v-slot:header.overtime_tier2>
            <span class="d-inline-block text-center header-multiline"
              >第3小時<br />後加班</span
            >
          </template>

          <!-- Regular Work Hours Slot (demo.html / month.html: 正常工時) -->
          <template v-slot:item.regular_hours="{ item }">
            <span class="text-sm text-body font-weight-bold">
              {{
                item.regular_hours !== undefined &&
                item.regular_hours !== null &&
                item.regular_hours !== ""
                  ? item.regular_hours
                  : "-"
              }}
            </span>
          </template>

          <!-- Overtime Tier 1 Slot (demo.html / month.html: 前2小時加班) -->
          <template v-slot:item.overtime_tier1="{ item }">
            <span class="text-sm text-body">
              {{
                item.overtime_tier1 !== undefined &&
                item.overtime_tier1 !== null &&
                item.overtime_tier1 !== ""
                  ? item.overtime_tier1
                  : ""
              }}
            </span>
          </template>

          <!-- Overtime Tier 2 Slot (demo.html / month.html: 第3小時後加班) -->
          <template v-slot:item.overtime_tier2="{ item }">
            <span class="text-sm text-body">
              {{
                item.overtime_tier2 !== undefined &&
                item.overtime_tier2 !== null &&
                item.overtime_tier2 !== ""
                  ? item.overtime_tier2
                  : ""
              }}
            </span>
          </template>

          <!-- Actions Slot -->
          <template v-slot:item.actions="{ item }">
            <div v-if="rootRole" class="d-flex align-center justify-center">
              <v-btn
                @click.stop="editItem(item)"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls me-2 my-2 rounded-sm"
                color="#67748e"
                title="編輯"
              >
                <v-icon size="18">mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                @click.stop="viewHistory(item)"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls me-2 my-2 rounded-sm"
                color="#cb0c9f"
                title="歷史記錄"
              >
                <v-icon size="18">mdi-history</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:body.append>
            <tr v-if="error">
              <td
                :colspan="headers.length"
                class="text-center red--text text-body-2"
              >
                {{ error }}
              </td>
            </tr>
            <tr v-else-if="!loading && !records.length">
              <td
                :colspan="headers.length"
                class="text-center grey--text text-body-2"
              >
                {{ selectedUserId ? "本月無資料" : "請選擇員工" }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

    <info-dialog
      :visible.sync="infoDialog.visible"
      :title="infoDialog.title"
      :message="infoDialog.message"
      :yes-button-text="infoDialog.yesButtonText"
      :yes-callback="infoDialog.yesCallback"
    />

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card class="card-shadow border-radius-xl" v-if="editedItem">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0">
            編輯打卡紀錄 - {{ editedItem.clock_date }}
          </span>
        </div>
        <v-card-text class="card-padding">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="clockInActualTime"
                label="實際上班時間 (HH:mm)"
                placeholder="08:00"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="clockOutActualTime"
                label="實際下班時間 (HH:mm)"
                placeholder="17:00"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="lunchBreakStartActualTime"
                label="休息A開始時間 (HH:mm)"
                placeholder="12:00"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="lunchBreakEndActualTime"
                label="休息A結束時間 (HH:mm)"
                placeholder="13:00"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="editedItem.late"
                label="遲到 (分鐘)"
                type="number"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="editedItem.early"
                label="早退 (分鐘)"
                type="number"
                dense
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn text @click="cancelEdit" class="mr-2">取消</v-btn>
          <v-btn color="primary" class="bg-gradient-primary" @click="saveItem">
            儲存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- History Dialog -->
    <v-dialog v-model="historyDialog" max-width="900px" scrollable>
      <v-card class="card-shadow border-radius-xl">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0">
            修改歷史記錄
          </span>
        </div>
        <v-card-text class="card-padding" style="max-height: 600px">
          <v-progress-linear
            v-if="historyLoading"
            indeterminate
            color="primary"
          />
          <div v-else-if="historyError" class="red--text text-center py-4">
            {{ historyError }}
          </div>
          <div
            v-else-if="!historyRecords.length"
            class="grey--text text-center py-4"
          >
            無修改記錄
          </div>
          <v-timeline v-else dense>
            <v-timeline-item
              v-for="record in historyRecords"
              :key="record.id"
              small
              color="primary"
            >
              <template v-slot:opposite>
                <span class="text-caption">
                  {{ formatDateTime(record.create_time) }}
                </span>
              </template>
              <v-card class="elevation-2">
                <v-card-text>
                  <div class="font-weight-bold mb-2">
                    修改人: {{ record.modifier ? record.modifier.name : "-" }}
                    <span
                      v-if="record.modifier && record.modifier.email"
                      class="text-caption grey--text"
                    >
                      ({{ record.modifier.email }})
                    </span>
                  </div>
                  <div class="text-caption grey--text mb-2">
                    {{ formatDateTime(record.create_time) }}
                  </div>
                  <v-divider class="my-2" />
                  <div class="text-body-2">
                    <div
                      v-for="(change, field) in record.changes"
                      :key="field"
                      class="mb-2"
                    >
                      <strong>{{ getFieldLabel(field) }}:</strong>
                      <div class="ml-4">
                        <div class="red--text text--lighten-1">
                          舊值: {{ formatFieldValue(field, change.old) }}
                        </div>
                        <div class="green--text text--darken-1">
                          新值: {{ formatFieldValue(field, change.new) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
          <div v-if="historyMeta.totalPages > 1" class="text-center mt-4">
            <v-pagination
              v-model="historyPage"
              :length="historyMeta.totalPages"
              @input="loadHistory"
              circle
              color="primary"
            />
          </div>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn
            @click="closeHistory"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
            "
          >
            關閉
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import InfoDialog from "@/components/Common/InfoDialog.vue";
import { formatDateTime as formatDateTimeUtil } from "@/utils/time";
import { LEAVE_TYPE } from "@/constants";

export default {
  name: "MonthlyClockRecordsV2",
  components: {
    InfoDialog,
  },
  props: {
    targetUserId: {
      type: Number,
      default: null,
    },
    hideUserSelect: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    return {
      activeTab: 0,
      apiDailyWorkHoursRows: null,
      apiMonthlyStats: null,
      apiAnnualSpecialSummary: null,
      records: [],
      holidays: [],
      annualLeaves: [],
      loading: false,
      loadingUsers: false,
      exporting: false,
      error: null,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      yearOptions: Array.from({ length: 10 }, (_, i) => currentYear - 5 + i),
      monthOptions: Array.from({ length: 12 }, (_, i) => i + 1),
      users: [],
      selectedUserId: this.targetUserId || null,

      currentPage: 1,
      pageCount: 0,
      itemsPerPage: 31,
      totalItems: 0,

      editDialog: false,
      editedItem: null,
      originalItem: null,
      clockInActualDate: null,
      clockInActualTime: null,
      clockOutActualDate: null,
      clockOutActualTime: null,
      lunchBreakStartActualDate: null,
      lunchBreakStartActualTime: null,
      lunchBreakEndActualDate: null,
      lunchBreakEndActualTime: null,

      activeGPSKey: null,

      // History
      historyDialog: false,
      historyLoading: false,
      historyError: null,
      historyRecords: [],
      historyMeta: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      historyPage: 1,
      currentHistoryRecordId: null,

      infoDialog: {
        visible: false,
        title: "",
        message: "",
        yesButtonText: "確定",
        yesCallback: null,
      },

      headers: [
        {
          text: "日期",
          value: "date",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "120px",
        },
        {
          text: "狀態",
          value: "timeline_status",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "60%",
        },
        {
          text: "備註",
          value: "notes",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "正常工時",
          value: "regular_hours",
          sortable: false,
          align: "center",
          class:
            "text-secondary font-weight-bolder opacity-7 black--text px-1 number-col-header",
          cellClass: "px-1 number-col-cell",
          width: "60px",
        },
        {
          text: "前2小時加班",
          value: "overtime_tier1",
          sortable: false,
          align: "center",
          class:
            "text-secondary font-weight-bolder opacity-7 black--text px-1 number-col-header",
          cellClass: "px-1 number-col-cell",
          width: "60px",
        },
        {
          text: "第3小時後加班",
          value: "overtime_tier2",
          sortable: false,
          align: "center",
          class:
            "text-secondary font-weight-bolder opacity-7 black--text px-1 number-col-header",
          cellClass: "px-1 number-col-cell",
          width: "60px",
        },
        {
          text: "操作",
          value: "actions",
          sortable: false,
          align: "center",
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "120px",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    ...mapGetters("user", ["rootRole"]),
    dailyWorkHoursRows() {
      if (this.apiDailyWorkHoursRows && Array.isArray(this.apiDailyWorkHoursRows) && this.apiDailyWorkHoursRows.length > 0) {
        return this.apiDailyWorkHoursRows;
      }
      if (!this.selectedYear || !this.selectedMonth) return [];
      const totalDays = new Date(
        this.selectedYear,
        this.selectedMonth,
        0,
      ).getDate();

      const days = [];
      const recordMap = new Map();
      if (Array.isArray(this.records)) {
        this.records.forEach((r) => {
          if (r.clock_date) {
            recordMap.set(r.clock_date, r);
          }
        });
      }

      const holidayMap = new Map();
      const normalizeDateStr = (dateVal) => {
        if (!dateVal) return null;
        const s = String(dateVal).trim();
        if (s.length === 8 && !s.includes("-")) {
          return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
        }
        return s.slice(0, 10);
      };

      if (Array.isArray(this.holidays)) {
        this.holidays.forEach((h) => {
          const dateKey = normalizeDateStr(
            h.actual_date || h.original_date || h.date,
          );
          if (dateKey) {
            holidayMap.set(dateKey, h);
          }
        });
      }

      for (let d = 1; d <= totalDays; d++) {
        const dateObj = new Date(this.selectedYear, this.selectedMonth - 1, d);
        const weekdayIdx = dateObj.getDay();
        const weekdayText = ["日", "一", "二", "三", "四", "五", "六"][
          weekdayIdx
        ];
        const isWeekend = weekdayIdx === 0 || weekdayIdx === 6;
        const dateString = `${this.selectedMonth}/${d}(${weekdayText})`;
        const monthStr = String(this.selectedMonth).padStart(2, "0");
        const dayStr = String(d).padStart(2, "0");
        const fullDate = `${this.selectedYear}-${monthStr}-${dayStr}`;

        const r = recordMap.get(fullDate);
        const holidayObj = holidayMap.get(fullDate);
        const isHoliday = Boolean(holidayObj || r?.is_holiday);
        const holidayName = holidayObj?.name || r?.holiday_name || "國定假日";

        let displayValue = "-";
        let isAlert = false;

        if (r) {
          const hasLeaves =
            Array.isArray(r.leave_records) && r.leave_records.length > 0;
          const hasActualPunches = Boolean(
            r.clock_in_actual || r.clock_out_actual,
          );

          if (hasLeaves && !hasActualPunches) {
            const firstLeave = r.leave_records[0];
            displayValue = this.getLeaveTypeName(firstLeave.leave_type);
            isAlert = true;
          } else if (hasActualPunches) {
            const breakdown = this.getWorkHoursBreakdown(r);
            const totalHours =
              (Number(breakdown.regular) || 0) +
              (Number(breakdown.ot1) || 0) +
              (Number(breakdown.ot2) || 0);

            displayValue = totalHours > 0 ? String(totalHours) : "-";
            if (
              (breakdown.ot1 && Number(breakdown.ot1) > 0) ||
              (breakdown.ot2 && Number(breakdown.ot2) > 0) ||
              isHoliday ||
              r.is_holiday ||
              r.is_recess ||
              totalHours > 8
            ) {
              isAlert = true;
            }
          } else if (isHoliday || r.is_holiday) {
            displayValue = "國定假日";
            isAlert = true;
          } else if (r.is_recess || isWeekend) {
            displayValue = weekdayIdx === 0 ? "例假日" : "休息日";
            isAlert = true;
          } else {
            displayValue = "缺勤";
            isAlert = true;
          }
        } else {
          if (isHoliday) {
            displayValue = "國定假日";
            isAlert = true;
          } else if (isWeekend) {
            displayValue = weekdayIdx === 0 ? "例假日" : "休息日";
            isAlert = true;
          } else {
            displayValue = "-";
            isAlert = false;
          }
        }

        days.push({
          day: d,
          dateString,
          fullDate,
          displayValue,
          isAlert,
          holidayName: isHoliday ? holidayName : "",
        });
      }

      // Split into rows of up to 16 days
      return [days.slice(0, 16), days.slice(16)];
    },
    monthlyStats() {
      if (this.apiMonthlyStats && typeof this.apiMonthlyStats === "object" && Object.keys(this.apiMonthlyStats).length > 0) {
        return this.apiMonthlyStats;
      }
      const stats = {
        regular_hours: 0,
        weekday_overtime: 0,
        national_overtime: 0,
        recess_overtime: 0,
        absent_hours: 0,
        late_count: 0,
        early_count: 0,
        personal_leave: 0,
        family_care_leave: 0,
        sick_leave: 0,
        menstrual_leave: 0,
        work_injury_leave: 0,
        official_leave: 0,
        maternity_leave: 0,
        prenatal_check_leave: 0,
        paternity_leave: 0,
        funeral_leave: 0,
        marriage_leave: 0,
        indigenous_leave: 0,
        annual_leave: 0,
      };

      if (!Array.isArray(this.records)) return stats;

      const holidayMap = new Map();
      const normalizeDateStr = (dateVal) => {
        if (!dateVal) return null;
        const s = String(dateVal).trim();
        if (s.length === 8 && !s.includes("-")) {
          return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
        }
        return s.slice(0, 10);
      };

      if (Array.isArray(this.holidays)) {
        this.holidays.forEach((h) => {
          const dateKey = normalizeDateStr(
            h.actual_date || h.original_date || h.date,
          );
          if (dateKey) {
            holidayMap.set(dateKey, h);
          }
        });
      }

      this.records.forEach((r) => {
        const breakdown = this.getWorkHoursBreakdown(r);
        const regH = Number(breakdown.regular) || 0;
        stats.regular_hours += regH;

        let otMinutes = 0;
        if (Array.isArray(r.overtimeRecords) && r.overtimeRecords.length > 0) {
          otMinutes = r.overtimeRecords.reduce(
            (acc, ot) => acc + (Number(ot.overtime_minutes) || 0),
            0,
          );
        } else if (r.overtime_minutes) {
          otMinutes = Number(r.overtime_minutes) || 0;
        }
        const otHours = Number((otMinutes / 60).toFixed(2));

        const dateKey = r.clock_date ? normalizeDateStr(r.clock_date) : null;
        const isHolidayDate = Boolean(
          r.is_holiday || (dateKey && holidayMap.has(dateKey)),
        );

        if (isHolidayDate) {
          stats.national_overtime += otHours;
        } else if (r.is_recess) {
          stats.recess_overtime += otHours;
        } else {
          stats.weekday_overtime += otHours;
        }

        if (r.late > 0) stats.late_count += 1;
        if (r.early > 0) stats.early_count += 1;

        if (
          !r.clock_in_actual &&
          !r.clock_out_actual &&
          (!r.leave_records || r.leave_records.length === 0) &&
          !isHolidayDate &&
          !r.is_recess
        ) {
          stats.absent_hours += 8;
        }

        if (Array.isArray(r.leave_records)) {
          r.leave_records.forEach((l) => {
            const hours = Number((l.leave_minutes / 60).toFixed(1)) || 0;
            switch (Number(l.leave_type)) {
              case 1:
                stats.annual_leave += hours;
                break;
              case 2:
                stats.personal_leave += hours;
                break;
              case 3:
                stats.sick_leave += hours;
                break;
              case 4:
                stats.menstrual_leave += hours;
                break;
              case 5:
                stats.work_injury_leave += hours;
                break;
              case 6:
                stats.marriage_leave += hours;
                break;
              case 7:
                stats.prenatal_check_leave += hours;
                break;
              case 8:
                stats.maternity_leave += hours;
                break;
              case 9:
                stats.paternity_leave += hours;
                break;
              case 10:
                stats.funeral_leave += hours;
                break;
              case 11:
                stats.official_leave += hours;
                break;
              case 12:
                stats.family_care_leave += hours;
                break;
              case 13:
                stats.indigenous_leave += hours;
                break;
            }
          });
        }
      });

      // Round numbers
      Object.keys(stats).forEach((k) => {
        stats[k] = Number(stats[k].toFixed(2));
      });

      return stats;
    },
    annualSpecialSummary() {
      if (
        this.apiAnnualSpecialSummary &&
        typeof this.apiAnnualSpecialSummary === "object" &&
        Object.keys(this.apiAnnualSpecialSummary).length > 0
      ) {
        const sickHours = this.apiAnnualSpecialSummary.sickHours ?? 0;
        const sickDays = this.apiAnnualSpecialSummary.sickDays ?? 0;
        return {
          sickHours,
          sickDays,
          sickCount: this.apiAnnualSpecialSummary.sickCount ?? 0,
          sickLeaveOverLimit:
            this.apiAnnualSpecialSummary.sickLeaveOverLimit !== undefined
              ? Boolean(this.apiAnnualSpecialSummary.sickLeaveOverLimit)
              : (sickHours > 80 || sickDays > 10),
          menstrualHours: this.apiAnnualSpecialSummary.menstrualHours ?? 0,
          menstrualDays: this.apiAnnualSpecialSummary.menstrualDays ?? 0,
          menstrualCount: this.apiAnnualSpecialSummary.menstrualCount ?? 0,
          familyCareHours: this.apiAnnualSpecialSummary.familyCareHours ?? 0,
          familyCareDays: this.apiAnnualSpecialSummary.familyCareDays ?? 0,
          familyCareCount: this.apiAnnualSpecialSummary.familyCareCount ?? 0,
        };
      }
      let sickMinutes = 0;
      let sickCount = 0;
      let menstrualMinutes = 0;
      let menstrualCount = 0;
      let familyCareMinutes = 0;
      let familyCareCount = 0;

      const sourceList =
        Array.isArray(this.annualLeaves) && this.annualLeaves.length > 0
          ? this.annualLeaves
          : this.records.flatMap((r) => r.leave_records || []);

      sourceList.forEach((l) => {
        const type = Number(l.leave_type);
        const mins = Number(l.leave_minutes) || 0;
        if (type === 3) {
          sickMinutes += mins;
          sickCount += 1;
        } else if (type === 4) {
          menstrualMinutes += mins;
          menstrualCount += 1;
        } else if (type === 12) {
          familyCareMinutes += mins;
          familyCareCount += 1;
        }
      });

      // 前24小時的生理假不計入病假，超過24小時的生理假併入病假計算
      const excessMenstrualMinutes = Math.max(0, menstrualMinutes - 24 * 60);
      const totalSickMinutes = sickMinutes + excessMenstrualMinutes;

      const sickHours = parseFloat((totalSickMinutes / 60).toFixed(1));
      const sickDays = parseFloat((totalSickMinutes / 480).toFixed(1));
      const menstrualHours = parseFloat((menstrualMinutes / 60).toFixed(1));
      const menstrualDays = parseFloat((menstrualMinutes / 480).toFixed(1));
      const familyCareHours = parseFloat((familyCareMinutes / 60).toFixed(1));
      const familyCareDays = parseFloat((familyCareMinutes / 480).toFixed(1));

      return {
        sickHours,
        sickDays,
        sickCount,
        sickLeaveOverLimit: sickHours > 80 || sickDays > 10,
        menstrualHours,
        menstrualDays,
        menstrualCount,
        familyCareHours,
        familyCareDays,
        familyCareCount,
      };
    },
  },
  watch: {
    targetUserId: {
      handler(val) {
        if (val) {
          this.selectedUserId = val;
          this.fetchAttendanceRecords();
          this.fetchAnnualLeaves();
        }
      },
      immediate: true,
    },
    selectedCompany: {
      handler(newVal) {
        if (newVal) {
          if (!this.targetUserId) {
            this.fetchUsers();
          }
          if (this.selectedUserId) {
            this.fetchAttendanceRecords();
            this.fetchAnnualLeaves();
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    formatDateWithWeekday(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekdays = ["日", "一", "二", "三", "四", "五", "六"];
      return `${month}/${day}(${weekdays[date.getDay()]})`;
    },
    formatTimeHHmm(value) {
      if (!value) return "";
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        if (typeof value === "string" && value.includes(":")) {
          const parts = value.split(":");
          return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
        }
        return value;
      }
      return date.toLocaleTimeString("zh-TW", {
        timeZone: "Asia/Taipei",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    formatDateTime(value) {
      return formatDateTimeUtil(value);
    },
    getLeaveTypeName(typeId) {
      return LEAVE_TYPE[typeId] || `假別${typeId}`;
    },
    getTimelineEvents(item) {
      if (!item) return [];
      if (Array.isArray(item.timeline_events) && item.timeline_events.length > 0) {
        return item.timeline_events;
      }
      const events = [];

      // Lateness indicator if late > 0 and scheduled clock-in exists
      if (item.late > 0 && item.clock_in_time) {
        events.push({
          id: `late_${item.id}`,
          type: "late",
          time: new Date(item.clock_in_time).getTime(),
          label: `遲到${this.formatTimeHHmm(item.clock_in_time)}`,
          isAlert: true,
        });
      }

      // Actual Clock In
      if (item.clock_in_actual) {
        events.push({
          id: `cin_${item.id}`,
          type: "clock_in",
          time: new Date(item.clock_in_actual).getTime(),
          label: `上班${this.formatTimeHHmm(item.clock_in_actual)}`,
          gps: item.gps_clock_in,
          gpsKey: `${item.id}_in`,
          gpsTitle: "GPS 位置 (上班)",
        });
      }

      // Lunch break actual start & end
      if (item.lunch_break_start_actual) {
        events.push({
          id: `lb_start_${item.id}`,
          type: "lunch_start",
          time: new Date(item.lunch_break_start_actual).getTime(),
          label: `休息A開始${this.formatTimeHHmm(
            item.lunch_break_start_actual,
          )}`,
          gps: item.gps_lunch_break_start,
          gpsKey: `${item.id}_lb_start`,
          gpsTitle: "GPS 位置 (午休開始)",
        });
      }
      if (item.lunch_break_end_actual) {
        events.push({
          id: `lb_end_${item.id}`,
          type: "lunch_end",
          time: new Date(item.lunch_break_end_actual).getTime(),
          label: `休息A結束${this.formatTimeHHmm(item.lunch_break_end_actual)}`,
          gps: item.gps_lunch_break_end,
          gpsKey: `${item.id}_lb_end`,
          gpsTitle: "GPS 位置 (午休結束)",
        });
      }

      // Rest 1 actual start & end
      if (item.rest_1_start_actual) {
        events.push({
          id: `rest1_start_${item.id}`,
          type: "rest_start",
          time: new Date(item.rest_1_start_actual).getTime(),
          label: `休息B開始${this.formatTimeHHmm(item.rest_1_start_actual)}`,
        });
      }
      if (item.rest_1_end_actual) {
        events.push({
          id: `rest1_end_${item.id}`,
          type: "rest_end",
          time: new Date(item.rest_1_end_actual).getTime(),
          label: `休息B結束${this.formatTimeHHmm(item.rest_1_end_actual)}`,
        });
      }

      // Leave records
      if (Array.isArray(item.leave_records)) {
        item.leave_records.forEach((leave) => {
          if (leave.start_time) {
            const leaveName = this.getLeaveTypeName(leave.leave_type);
            const startStr = this.formatTimeHHmm(leave.start_time);
            const endStr = leave.end_time
              ? this.formatTimeHHmm(leave.end_time)
              : "";
            const timeRange = endStr ? `${startStr}-${endStr}` : startStr;
            const hours = leave.leave_minutes
              ? parseFloat((leave.leave_minutes / 60).toFixed(1))
              : 0;

            const labelText =
              !item.clock_in_actual && !item.clock_out_actual && hours >= 8
                ? `${leaveName}${hours}小時`
                : `${leaveName}${timeRange}`;

            events.push({
              id: `leave_${item.id}_${leave.id}`,
              type: "leave",
              time: new Date(leave.start_time).getTime(),
              label: labelText,
              isAlert: true,
            });
          }
        });
      }

      // Overtime records
      if (Array.isArray(item.overtimeRecords)) {
        item.overtimeRecords.forEach((ot) => {
          if (ot.start_time) {
            const startStr = this.formatTimeHHmm(ot.start_time);
            const endStr = ot.end_time ? this.formatTimeHHmm(ot.end_time) : "";
            const timeRange = endStr ? `${startStr}-${endStr}` : startStr;
            events.push({
              id: `ot_${item.id}_${ot.id}`,
              type: "overtime",
              time: new Date(ot.start_time).getTime(),
              label: `加班${timeRange}`,
            });
          }
        });
      }

      // Actual Clock Out
      if (item.clock_out_actual) {
        events.push({
          id: `cout_${item.id}`,
          type: "clock_out",
          time: new Date(item.clock_out_actual).getTime(),
          label: `下班${this.formatTimeHHmm(item.clock_out_actual)}`,
          gps: item.gps_clock_out,
          gpsKey: `${item.id}_out`,
          gpsTitle: "GPS 位置 (下班)",
        });
      }

      // Missing punches / absent state fallback
      if (events.length === 0) {
        if (item.clock_in_time && item.clock_out_time) {
          const shiftStart = this.formatTimeHHmm(item.clock_in_time);
          const shiftEnd = this.formatTimeHHmm(item.clock_out_time);
          events.push({
            id: `absent_${item.id}`,
            type: "absent",
            time: new Date(item.clock_in_time).getTime(),
            label: `排班${shiftStart}-${shiftEnd} (未打卡)`,
            isAlert: true,
          });
        } else {
          events.push({
            id: `absent_${item.id}`,
            type: "absent",
            time: 0,
            label: "未打卡",
            isAlert: true,
          });
        }
      }

      // Type priorities when times match: actual punches rank before leaves and overtime
      const typePriority = {
        late: 1,
        clock_in: 2,
        lunch_start: 3,
        lunch_end: 4,
        rest_start: 5,
        rest_end: 6,
        clock_out: 7,
        leave: 8,
        overtime: 9,
        absent: 10,
      };

      // Sort by minute, then by type priority (punches before leave/overtime), then by timestamp
      events.sort((a, b) => {
        const minA = Math.floor((a.time || 0) / 60000);
        const minB = Math.floor((b.time || 0) / 60000);
        if (minA !== minB) {
          return minA - minB;
        }
        const prioA = typePriority[a.type] || 99;
        const prioB = typePriority[b.type] || 99;
        if (prioA !== prioB) {
          return prioA - prioB;
        }
        return (a.time || 0) - (b.time || 0);
      });
      return events;
    },
    getWorkHoursBreakdown(item) {
      if (!item) return { regular: "", ot1: "", ot2: "", workHours: "" };

      return {
        regular:
          item.regular_hours !== null &&
          item.regular_hours !== undefined &&
          item.regular_hours !== ""
            ? item.regular_hours
            : "",
        ot1:
          item.overtime_tier1 !== null &&
          item.overtime_tier1 !== undefined &&
          item.overtime_tier1 !== ""
            ? item.overtime_tier1
            : "",
        ot2:
          item.overtime_tier2 !== null &&
          item.overtime_tier2 !== undefined &&
          item.overtime_tier2 !== ""
            ? item.overtime_tier2
            : "",
        workHours:
          item.work_hours !== null &&
          item.work_hours !== undefined &&
          item.work_hours !== ""
            ? item.work_hours
            : "",
      };
    },
    getNotesInfo(item) {
      if (!item) return [];
      if (Array.isArray(item.notes_info) && item.notes_info.length > 0) {
        return item.notes_info;
      }
      if (Array.isArray(item.notes) && item.notes.length > 0) {
        return item.notes;
      }
      const notes = [];

      // 1. Leave summaries
      if (Array.isArray(item.leave_records) && item.leave_records.length > 0) {
        item.leave_records.forEach((l) => {
          const lName = this.getLeaveTypeName(l.leave_type);
          const hours = l.leave_minutes
            ? parseFloat((l.leave_minutes / 60).toFixed(1))
            : 0;
          notes.push({ text: `${lName}${hours}小時`, isAlert: true });
        });
      }

      // 2. Overtime status
      const hasOvertime =
        (Array.isArray(item.overtimeRecords) &&
          item.overtimeRecords.length > 0) ||
        item.overtime === true;
      if (!hasOvertime) {
        if (item.clock_in_actual || (item.status && item.status.includes(2))) {
          notes.push({ text: "無加班", color: "#548dd4" });
        }
      } else {
        if (item.is_holiday) {
          notes.push({ text: "國定假日加班", isAlert: true });
        } else if (item.is_recess) {
          notes.push({ text: "休息日加班", isAlert: true });
        }
      }

      // 3. Extra notes
      if (typeof item.extra === "object" && item.extra) {
        Object.entries(item.extra).forEach(([k, v]) => {
          if (k === "re_clock_in") {
            notes.push({ text: `請假期間上班: ${this.formatTimeHHmm(v)}` });
          } else if (k === "re_clock_out") {
            notes.push({ text: `請假期間下班: ${this.formatTimeHHmm(v)}` });
          } else {
            notes.push({ text: String(v) });
          }
        });
      } else if (item.extra) {
        notes.push({ text: String(item.extra) });
      }

      // 4. Absent note
      if (
        !item.clock_in_actual &&
        !item.clock_out_actual &&
        (!item.leave_records || item.leave_records.length === 0)
      ) {
        if (item.is_holiday) {
          notes.push({ text: item.holiday_name || "國定假日", isAlert: true });
        } else if (item.is_recess) {
          notes.push({ text: "休息日", isAlert: true });
        } else {
          notes.push({ text: "缺勤", isAlert: true });
        }
      }

      return notes;
    },
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const companyId = this.selectedCompany ? this.selectedCompany.id : null;
        if (!companyId) {
          this.users = [];
          return;
        }
        const allUsers = [];
        const seenUserIds = new Set();
        let page = 1;
        const limit = 100;
        let hasNextPage = true;

        while (hasNextPage) {
          const params = new URLSearchParams({
            company_id: companyId,
            page: String(page),
            limit: String(limit),
          });
          const response = await this.$axios.get(`/api/users?${params}`);
          const payload = response.data;
          const pageUsers = Array.isArray(payload)
            ? payload
            : Array.isArray(payload.data)
            ? payload.data
            : [];

          pageUsers.forEach((user) => {
            if (!user || user.id == null || seenUserIds.has(user.id)) return;
            seenUserIds.add(user.id);
            allUsers.push(user);
          });

          const pagination = payload?.pagination || null;
          hasNextPage = Boolean(
            pagination?.hasNextPage || pagination?.nextPage,
          );
          page = pagination?.nextPage || page + 1;
        }

        this.users = allUsers;
      } catch (error) {
        console.error("Fetch users error:", error);
      } finally {
        this.loadingUsers = false;
      }
    },
    onUserChange() {
      this.currentPage = 1;
      this.fetchAttendanceRecords();
      this.fetchAnnualLeaves();
    },
    onDateChange() {
      if (this.selectedUserId) {
        this.currentPage = 1;
        this.fetchAttendanceRecords();
        this.fetchAnnualLeaves();
      }
    },
    async fetchAttendanceRecords() {
      if (!this.selectedUserId) {
        return;
      }
      this.loading = true;
      this.error = null;

      const startDate = new Date(this.selectedYear, this.selectedMonth - 1, 1);
      const endDate = new Date(this.selectedYear, this.selectedMonth, 0);

      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const queryParams = new URLSearchParams({
        page: this.currentPage,
        limit: this.itemsPerPage,
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        company_id: this.selectedCompany ? this.selectedCompany.id : "",
        user_id: this.selectedUserId,
      });

      try {
        const response = await this.$axios.get(
          `/api/clock-records/v2?${queryParams}`,
        );

        const newRecords = response.data.rows || [];
        this.records = newRecords;
        this.apiDailyWorkHoursRows = response.data.dailyWorkHoursRows || null;
        this.apiMonthlyStats = response.data.monthlyStats || null;
        this.apiAnnualSpecialSummary = response.data.annualSpecialSummary || null;
        this.holidays =
          response.data.holidays || response.data.holiday?.holidays || [];
        this.pageCount = response.data.pagination
          ? response.data.pagination.totalPages
          : 1;
        this.totalItems = response.data.pagination
          ? response.data.pagination.totalItems
          : 0;
      } catch (error) {
        this.records = [];
        this.holidays = [];
        this.apiDailyWorkHoursRows = null;
        this.apiMonthlyStats = null;
        this.apiAnnualSpecialSummary = null;
        this.error =
          error?.response?.data?.message ||
          error.message ||
          "取得資料時發生錯誤";
      } finally {
        this.loading = false;
      }
    },
    async fetchAnnualLeaves() {
      if (!this.selectedUserId || !this.selectedCompany) return;
      try {
        const params = new URLSearchParams({
          company_id: this.selectedCompany.id,
          user_id: this.selectedUserId,
          year: String(this.selectedYear),
          limit: "500",
        });
        const res = await this.$axios.get(`/api/leave-records/user?${params}`);
        this.annualLeaves = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];
      } catch (err) {
        console.error("fetchAnnualLeaves error:", err);
        this.annualLeaves = [];
      }
    },
    async exportExcelV2() {
      if (!this.selectedCompany || !this.selectedUserId) return;
      try {
        this.exporting = true;
        const companyId = this.selectedCompany.id;
        const year = this.selectedYear;
        const month = this.selectedMonth;
        const userId = this.selectedUserId;

        const response = await this.$axios.get(
          `/api/clock-records/export-month-v2?company_id=${companyId}&year=${year}&month=${month}&user_id=${userId}`,
          { responseType: "blob" },
        );

        const targetUser = this.users.find((u) => u.id === userId);
        const userName = targetUser?.name || "員工";
        const companyName = this.selectedCompany?.company_name || "";
        const defaultFilename = `出勤紀錄表_${year}年${month}月_${companyName}_${userName}.xlsx`;

        const blob = new Blob([response.data], {
          type:
            response.headers?.["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = defaultFilename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Export error:", err);
      } finally {
        this.exporting = false;
      }
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
      this.fetchAttendanceRecords();
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.originalItem = Object.assign({}, item);
      this.initializePickers();
      this.editDialog = true;
    },
    initializePickers() {
      if (!this.editedItem) return;
      this.clockInActualTime = this.formatTimeHHmm(
        this.editedItem.clock_in_actual,
      );
      this.clockOutActualTime = this.formatTimeHHmm(
        this.editedItem.clock_out_actual,
      );
      this.lunchBreakStartActualTime = this.formatTimeHHmm(
        this.editedItem.lunch_break_start_actual,
      );
      this.lunchBreakEndActualTime = this.formatTimeHHmm(
        this.editedItem.lunch_break_end_actual,
      );
    },
    combineDateTime(dateStr, timeStr) {
      if (!dateStr || !timeStr) return null;
      return new Date(`${dateStr}T${timeStr}:00+08:00`).toISOString();
    },
    cancelEdit() {
      this.editDialog = false;
      this.editedItem = null;
    },
    async saveItem() {
      if (!this.editedItem) return;
      this.loading = true;
      try {
        const dateStr = this.editedItem.clock_date;
        const payload = {
          clock_in_actual: this.clockInActualTime
            ? this.combineDateTime(dateStr, this.clockInActualTime)
            : null,
          clock_out_actual: this.clockOutActualTime
            ? this.combineDateTime(dateStr, this.clockOutActualTime)
            : null,
          lunch_break_start_actual: this.lunchBreakStartActualTime
            ? this.combineDateTime(dateStr, this.lunchBreakStartActualTime)
            : null,
          lunch_break_end_actual: this.lunchBreakEndActualTime
            ? this.combineDateTime(dateStr, this.lunchBreakEndActualTime)
            : null,
        };

        if (this.editedItem.late !== this.originalItem.late) {
          payload.late = this.editedItem.late;
        }
        if (this.editedItem.early !== this.originalItem.early) {
          payload.early = this.editedItem.early;
        }

        const response = await this.$axios.put(
          `/api/clock-records/${this.editedItem.id}`,
          payload,
        );

        const updatedData = response.data.data;
        const index = this.records.findIndex(
          (r) => r.id === this.editedItem.id,
        );
        if (index !== -1 && updatedData) {
          this.records.splice(index, 1, updatedData);
        }

        this.infoDialog = {
          visible: true,
          title: "成功",
          message: "更新打卡紀錄成功",
          yesButtonText: "確定",
          yesCallback: () => {
            this.infoDialog.visible = false;
          },
        };
        this.editDialog = false;
        this.editedItem = null;
      } catch (error) {
        this.infoDialog = {
          visible: true,
          title: "錯誤",
          message:
            error?.response?.data?.message ||
            error.message ||
            "更新打卡紀錄失敗",
          yesButtonText: "確定",
          yesCallback: () => {
            this.infoDialog.visible = false;
          },
        };
      } finally {
        this.loading = false;
      }
    },
    async viewHistory(item) {
      this.currentHistoryRecordId = item.id;
      this.historyDialog = true;
      this.historyPage = 1;
      await this.loadHistory();
    },
    async loadHistory() {
      if (!this.currentHistoryRecordId) return;

      this.historyLoading = true;
      this.historyError = null;

      try {
        const queryParams = new URLSearchParams({
          page: this.historyPage,
          limit: this.historyMeta.limit,
        });

        const response = await this.$axios.get(
          `/api/history/clock-records/${this.currentHistoryRecordId}?${queryParams}`,
        );

        this.historyRecords = response.data.data || [];
        this.historyMeta = response.data.meta || this.historyMeta;
      } catch (error) {
        this.historyError =
          error?.response?.data?.message || error.message || "取得歷史記錄失敗";
      } finally {
        this.historyLoading = false;
      }
    },
    closeHistory() {
      this.historyDialog = false;
      this.historyRecords = [];
      this.currentHistoryRecordId = null;
    },
    getFieldLabel(field) {
      const labels = {
        clock_in_actual: "實際上班",
        clock_out_actual: "實際下班",
        clock_in_time: "上班時間",
        clock_out_time: "下班時間",
        lunch_break: "午休",
        lunch_break_start: "午休開始",
        lunch_break_end: "午休結束",
        lunch_break_start_actual: "午休實際開始",
        lunch_break_end_actual: "午休實際結束",
        overtime: "加班",
        overtime_record_id: "加班申請",
        leave_record_ids: "請假紀錄",
        extra: "備註",
        late: "遲到",
        early: "早退",
      };
      return labels[field] || field;
    },
    formatFieldValue(field, value) {
      if (value === null || value === undefined) return "-";
      const dateTimeFields = [
        "clock_in_actual",
        "clock_out_actual",
        "clock_in_time",
        "clock_out_time",
        "lunch_break_start_actual",
        "lunch_break_end_actual",
        "lunch_break_start",
        "lunch_break_end",
      ];
      if (dateTimeFields.includes(field)) {
        return this.formatDateTime(value);
      }
      if (field === "lunch_break") return value ? "是" : "否";
      if (field === "overtime")
        return value === true ? "是" : value === false ? "否" : "-";
      if (typeof value === "number") return value.toString();
      return value;
    },
    isValidGPS(gps) {
      if (!gps || typeof gps !== "string") return false;
      return gps.includes(",");
    },
    openGPSInGoogleMaps(gps) {
      if (!this.isValidGPS(gps)) return;
      const [lat, lon] = gps.split(",");
      const url = `https://www.google.com/maps/search/?api=1&query=${lat.trim()},${lon.trim()}`;
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
.matrix-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #d3d3d3;
}
.matrix-table th {
  background-color: #f2f2f2;
  border: 1px solid #d3d3d3;
  color: #344767;
  white-space: nowrap;
}
.matrix-table td {
  border: 1px solid #e0e0e0;
  white-space: nowrap;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
  border: 1.5pt solid #000000;
}
.stats-table td {
  border: 1px solid #d3d3d3;
  padding: 6px 8px;
  font-size: 0.875rem;
}
.stat-hdr {
  background-color: #f2f2f2;
  font-weight: 700;
  text-align: center;
  color: #344767;
  white-space: nowrap;
}
.stat-val {
  text-align: center;
  white-space: nowrap;
  min-width: 45px;
}

.table >>> thead th {
  white-space: nowrap;
  font-size: 1rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: #8392ab;
  opacity: 0.7;
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}
.table >>> tbody tr:last-child td {
  border-bottom: 0;
}
.table >>> tbody td {
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}
.table >>> tbody tr:hover {
  background: #f8f9fa;
}

.table >>> thead th.number-col-header {
  white-space: normal !important;
  line-height: 1.15 !important;
  font-size: 0.75rem !important;
  padding-left: 2px !important;
  padding-right: 2px !important;
  text-align: center !important;
}

.table >>> tbody td.number-col-cell {
  padding-left: 2px !important;
  padding-right: 2px !important;
  white-space: nowrap !important;
  text-align: center !important;
}

.header-multiline {
  line-height: 1.15;
  letter-spacing: 0;
  word-break: keep-all;
  font-size: 0.75rem;
}
</style>
